import { postKmsData, translate } from "@kaltura/mediaspace-shared-utils";
import { Camera24Icon } from "@kaltura/ds-react-icons";
import { useConfig } from "@kaltura/mediaspace-shared-data-kms-config";
import { UploadedMediaData } from "./types";
import { VideoSourceMetadata } from "./VideoSourceMetadata";
import { InPlaceUploadField, UploadPreviewPlayer } from "@kaltura/mediaspace-shared-in-place-upload";
import { SearchAndSelect, SearchAndSelectProps } from "@kaltura/mediaspace-shared-search-and-select";
import { Ref, useState } from "react";
import { Button, Modal, ModalContent, ModalTitle } from "@kaltura/ds-react-components";
import { KalturaBaseEntry } from "kaltura-typescript-client/api/types/KalturaBaseEntry";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts";
import { ButtonAnalyticsProvider, useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";

const analyticsLocation = "Video source selection";

export interface VideoSourceSectionProps {
    uploadVideoSourceUrl: string;
    setVideoSourceUrl: string;
    checkVideoSourceStatusUrl: string;
    initialMedia?: UploadedMediaData;
    searchAndSelect: SearchAndSelectProps;
    buttonRef?: Ref<HTMLButtonElement>;
    onBlur?: () => void;
    onDataChange?: (data: UploadedMediaData | undefined) => void;
    onProcessingChange?: (processing: boolean) => void;
}

/**
 * Simulive video source section for the Streaming tab and Simulive creation modal.
 */
export const VideoSourceSection = ({
    uploadVideoSourceUrl,
    setVideoSourceUrl,
    checkVideoSourceStatusUrl,
    initialMedia,
    searchAndSelect,
    buttonRef,
    onBlur,
    onDataChange,
    onProcessingChange,
}: VideoSourceSectionProps) => {
    const config = useConfig();
    const { serviceUrl, uploadKS } = config.application;

    const [selectedMedia, setSelectedMedia] = useState(initialMedia);

    const [showSns, setShowSns] = useState(false);
    const [processedSnsEntryId, setProcessedSnsEntryId] = useState<string>();

    const { showToast } = useToastsContext();
    const sendButtonAnalytics = useButtonAnalytics();

    if (!uploadKS) {
        throw new Error("application.uploadKS must be set for the edit media page");
    }

    const openSns = () => {
        setShowSns(true);
        sendButtonAnalytics(
            "Open video source selector",
            ButtonClickAnalyticsType.OPEN,
            undefined,
            undefined,
            analyticsLocation
        );
    };
    const closeSns = (sendCloseAnalytics = true) => {
        setShowSns(false);
        if (sendCloseAnalytics) {
            sendButtonAnalytics(
                "Close video source selector",
                ButtonClickAnalyticsType.CLOSE,
                undefined,
                undefined,
                analyticsLocation
            );
        }
    };

    const handleSelectionChange = async (entry: KalturaBaseEntry) => {
        const showError = () =>
            showToast({
                severity: "error",
                message: translate("Failed to set video source"),
            });

        setProcessedSnsEntryId(entry.id);
        try {
            const { success, data } = await postKmsData(setVideoSourceUrl, { id: entry.id });
            if (success) {
                setSelectedMedia(data);
                closeSns(false);
                sendButtonAnalytics(
                    "Select video source",
                    ButtonClickAnalyticsType.CHOOSE,
                    undefined,
                    undefined,
                    analyticsLocation
                );
            }
            else {
                showError();
            }
        }
        catch (e: unknown) {
            console.error(e);
            showError();
        }
        finally {
            setProcessedSnsEntryId(undefined);
        }
    };

    return (
        <ButtonAnalyticsProvider location={analyticsLocation}>
            <InPlaceUploadField
                id={"media"}
                height={"180px"}
                width={"320px"}
                label={translate("Video source")}
                description={translate(
                    "To ensure a seamless broadcast, upload your pre-recorded video ahead of the event."
                )}
                text={translate("Upload your pre-recorded video")}
                inline={true}
                icon={<Camera24Icon />}
                acceptedTypes={"video/*"}
                serviceUrl={serviceUrl}
                ks={uploadKS}
                createMediaUrl={uploadVideoSourceUrl}
                checkStatusUrl={checkVideoSourceStatusUrl}
                editButtonLabel={translate("Upload video")}
                editMenuLabel={translate("Upload new video")}
                outsideButton={true}
                metadataComponent={VideoSourceMetadata}
                previewComponent={UploadPreviewPlayer}
                data={selectedMedia}
                additionalActions={[{ label: translate("Select from My Media"), callback: openSns }]}
                buttonRef={buttonRef}
                onBlur={onBlur}
                onDataChange={onDataChange}
                onProcessingChange={onProcessingChange}
            />

            {showSns && (
                <Modal open={true} onClose={() => closeSns()} variant={"task"} maxWidth={"xl"}>
                    <ModalTitle onClose={() => closeSns()}>{translate("Select from My Media")}</ModalTitle>
                    <ModalContent>
                        <SearchAndSelect
                            {...searchAndSelect}
                            selectionMode={"single"}
                            showExploreButton={false}
                            actions={({ entry }) => (
                                <Button
                                    type={"button"}
                                    onClick={() => handleSelectionChange(entry)}
                                    loading={processedSnsEntryId === entry.id}
                                    disabled={!!processedSnsEntryId}
                                >
                                    {translate("Select")}
                                </Button>
                            )}
                        />
                    </ModalContent>
                </Modal>
            )}
        </ButtonAnalyticsProvider>
    );
};
